import { Box, Stack } from "@mui/material";
import { isEmpty } from "lodash-es";
import { useMemo, useRef, type FC } from "react";
import { type UUID } from "../../../../utils/uuid";
import { makeDefaultLexicalDesign } from "../../../lexical-chat-moment/make-default-lexical-design";
import { type ToolbarSettings } from "../../../lexical-email-moment/hooks/use-email-toolbar-settings";
import { useFilterTags } from "../../../merge-tag/components/use-filter-tags";
import type { Tags } from "../../../merge-tag/types";
import { DefaultBodyPlugins } from "../../../message/editor/default-body-plugins";
import { DefaultContentEditable } from "../../../message/editor/default-content-editable";
import { DefaultPlaceholder } from "../../../message/editor/default-placeholder";
import { useLexicalContext } from "../../../message/editor/lexical-context";
import { LexicalEditorWrapper } from "../../../message/editor/lexical-editor-wrapper";
import { FixSmsSurveyLinkPlugin } from "../../../message/editor/plugins/fix-sms-survey-link-plugin";
import { GenericToolbar } from "../../../message/editor/plugins/generic-toolbar/generic-toolbar";
import type { SerialisedLexicalEditorDesign } from "../../../message/editor/types/serialised-editor-design";
import type { ToTarget } from "../../../to/use-to-target";
import { useHandleFollowUpLexicalError } from "../../hooks/use-handle-follow-up-lexical-error";

interface FollowUpMessageSmsProps {
  moment_id: UUID;
  mergeTags: Tags;
  onChange: (val: SerialisedLexicalEditorDesign) => void;
  toTarget: ToTarget;
  design: SerialisedLexicalEditorDesign | null;
  errors: { design?: string };
  disabled: boolean;
}

export const FollowUpMessageSms: FC<FollowUpMessageSmsProps> = ({
  moment_id,
  mergeTags,
  onChange,
  toTarget,
  design,
  errors,
  disabled,
}) => {
  const ref = useRef<HTMLDivElement>(null);

  const onError = useHandleFollowUpLexicalError(moment_id);

  const filterTags = useFilterTags(
    mergeTags.filter((x) => x.version === 2),
    toTarget,
  );

  const toolbarSettings: ToolbarSettings = useMemo(
    () => ({
      topLevelElements: [
        { type: "mergeTagPicker" },
        { type: "emojiPicker" },
        { type: "surveyButton", channel: "twilio" } as const,
      ],
      floatingElements: {
        surveyLink: [{ type: "linkButton" } as const],
      },
      rightAlignedElements: [{ type: "characterCount" }],
      channel: "text" as const,
    }),
    [],
  );

  return (
    <Stack direction="column" spacing={2}>
      <LexicalEditorWrapper
        channelName="twilio"
        onError={(...args) => void onError(...args).catch(console.error)}
        error={errors.design}
        filterTags={filterTags}
        format="sms"
        design={isEmpty(design) ? makeDefaultLexicalDesign("sms") : design}
        disabled={disabled}
        renderLayout={(editor) => (
          <Box
            sx={{
              // Override the default anchor link styles for Twilio's lexical survey links.
              "& .editor-content.twilio a.survey-link": {
                backgroundColor: "#E2FF53",
                p: 0,
                borderRadius: 0,
                color: "black",
                fontWeight: "normal",
                textDecoration: "none",
                fontSize: "inherit",
              },
            }}
            ref={ref}
          >
            {editor}
          </Box>
        )}
        slots={{
          contentEditable: ({ focusEditor, onRef }) => (
            <DefaultContentEditable
              focusEditor={focusEditor}
              onRef={onRef}
              channel={"twilio"}
              disabled={disabled}
            />
          ),
          placeholder: <DefaultPlaceholder />,
          plugins: {
            head: () => <TextHeadPlugins toolbarSettings={toolbarSettings} disabled={disabled} />,
            body: (historyState) => (
              <>
                <DefaultBodyPlugins
                  auto_focus={false}
                  channel={"twilio"}
                  format="sms"
                  disabled={disabled}
                  onChange={(val) => onChange(val.toJSON())}
                  editorScrollRef={ref}
                  toolbarSettings={toolbarSettings}
                  historyState={historyState}
                  v1Tags={mergeTags.filter((x) => x.version === 1)}
                />
                <FixSmsSurveyLinkPlugin />
              </>
            ),
          },
        }}
      />
    </Stack>
  );
};

const TextHeadPlugins: FC<{
  toolbarSettings: ToolbarSettings;
  disabled?: boolean;
}> = ({ toolbarSettings, disabled }) => {
  // This will eventually be rendered by the LexicalEditor calling the slot, so we can
  // use the context here
  const {
    editorModal: { handleOpen },
  } = useLexicalContext();

  return (
    <>
      <GenericToolbar
        channel={toolbarSettings.channel}
        rightAlignedElements={toolbarSettings.rightAlignedElements}
        topLevelElements={toolbarSettings.topLevelElements}
        disabled={!!disabled}
        openModal={handleOpen}
      />
    </>
  );
};
